<template>
    <div>
        <v-row justify="center" class="bg-color-teal-900">
            <v-col cols="0" sm="2"></v-col>
            <v-col cols="12" sm="8">
                <v-row>
                    <v-container>
                        <div class="body-text text-white mt-4">
                            <p class="text-h4 font-weight-light">
                                <LanguagePlaceholder is-html default-text="Credit Note Report" />
                            </p>
                            <br />
                            <p>
                                <b>WHAT is this report: </b>
                                Your Credit Note report details each of the Credit Notes that have been issued to you for your partners. The report shows the value in both local currency and USD. 
                                <br /><br />
                                <b>HOW do you use it: </b>
                                The "Comment" column will detail either if the Credit Note is paid out for direct RISE rewards (<b>Earned Credit Note</b>), or if the Credit Note is issued in lieu of a Utilized E-Voucher. If it is an Earned Credit Note, please contact your partners to let them know that a Credit has been applied to their account with you. If it is a Credit Note reimbursement for E-vouchers, the E-voucher code will appear here for your reconciliation.
                            </p>
                            <PleaseNote></PleaseNote>
                        </div>
                    </v-container>
                    <v-container>
                        <v-row class="">
                            <v-col cols="12" sm="6" md="4">
                                <div class="d-flex ga-5">
                                    <v-switch 
                                        v-model="isSelectByQuarter"
                                        @update:modelValue="selectByQuarter"
                                        color="#9FDDFF"
                                        density="compact">
                                    </v-switch>
                                    <v-select class="text-white" label="Select Incentive Period"
                                        v-model="selectedQuarters" :items="quarters" variant="outlined" multiple chips
                                        closable-chips
                                        aria-label="Select one or more incentive periods"
                                        :disabled="!isSelectByQuarter">
                                        <template v-slot:label>
                                            <LanguagePlaceholder is-html default-text="Select Incentive Period" />
                                        </template>
                                    </v-select>
                                </div>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <div>
                                    <v-select clearable :items="countries" return-object v-model="selectedCountries"
                                        class="text-white" item-title="country" item-value="country" variant="outlined"
                                        multiple chips closable-chips
                                        aria-label="Select one or more locations"
                                        :disabled="!isSelectByQuarter">
                                        <template v-slot:label>
                                            <LanguagePlaceholder is-html default-text="Select Location" />
                                        </template>
                                    </v-select>
                                </div>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6" md="4">
                                <div class="d-flex ga-5">
                                    <v-switch 
                                        v-model="isSelectByNumber"
                                        @update:modelValue="selectByNumber"
                                        color="#9FDDFF"
                                        density="compact">
                                    </v-switch>
                                    <v-text-field clearable label="Credit Note Number" class="text-white" v-model="creditNoteNumber"
                                        variant="outlined" :disabled="!isSelectByNumber"></v-text-field>                                    
                                </div>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                            </v-col>
                        </v-row>
                        <v-row class="gap-2 align-center mb-5">
                            <v-btn @click="clickSubmit" color="white" variant="outlined">
                                <LanguagePlaceholder default-text="Submit" />
                            </v-btn>
                            <v-btn class="mx-4" prepend-icon="mdi-download" @click="downloadFile" color="white" variant="outlined" aria-label="Download Report"><LanguagePlaceholder default-text="Download" /></v-btn>
                        </v-row>
                    </v-container>                    
                </v-row>
            </v-col>
            <v-col cols="0" sm="2"></v-col>
        </v-row>
        <v-row justify="center" style="background-color: #ffffff;" class="pb-6">
            <v-container>
                <v-text-field v-model="searchString" label="Filter" prepend-inner-icon="mdi-filter-outline"
                    variant="outlined" hide-details single-line aria-label="Filter credit note data"></v-text-field> 
                <v-data-table items-per-page="50" :headers="headersCreditNotes" :items="reportData" :search="searchString"
                    item-key="id" class="v-data-table" role="table" aria-label="Credit note report data">
                    <template v-slot:item.totalPayoutRounded="{item}">
                        <div>{{$filters.currencyUSD(item.totalPayoutRounded)}}</div>
                    </template>
                    <template v-slot:item.totalPayoutLCRounded="{item}">
                        <div>{{$filters.toGenericCurrency(item.totalPayoutLCRounded)}}</div>
                    </template>
                </v-data-table>
            </v-container>
        </v-row>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useSystemStore } from '@/stores/system'
import { useRegistrationStore } from '@/stores/registration'
import LanguagePlaceholder from '@/components/LanguagePlaceholder.vue'
import { useExcelStore } from '@/stores/excel'
import PleaseNote from '@/components/PleaseNote.vue'

const systemStore = useSystemStore()
const registrationStore = useRegistrationStore()
const excelStore = useExcelStore()

const quarters = ref([]);
const selectedQuarters = ref([])
const selectedCountries = ref([])
const currentQuarter = ref('')
const selectedDistributorAffinityId = ref('')

const creditNoteNumber = ref('')
const isSelectByQuarter = ref(true)
const isSelectByNumber = ref(false)

const affinityId = ref(0)
const countries = ref([])
const reportData = ref([])
const searchString = ref('')

const headersCreditNotes = [
    { title: 'Quarter', value: 'quarter', headerProps: { class: 'header-style' }, sortable: true },
    { title: 'Partner Affinity Id', value: 'partnerAffinityId', headerProps: { class: 'header-style' }, sortable: true },
    { title: 'Partner Name', value: 'partnerAccountName', headerProps: { class: 'header-style' }, sortable: true },
    { title: 'Location', value: 'partnerCountry', headerProps: { class: 'header-style' } },
    { title: 'Local Currency Code', value: 'currencyCode', headerProps: { class: 'header-style' } },
    { title: 'Total Rebate Amount (LC)', value: 'totalPayoutLCRounded', align: 'center', headerProps: { class: 'header-style text-left' }, sortable: true },
    { title: 'Total Rebate Amount (USD)', value: 'totalPayoutRounded', align: 'center', headerProps: { class: 'header-style' }, sortable: true },
    { title: 'Credit Note Number', value: 'creditNoteNumber', headerProps: { class: 'header-style' }, sortable: true },
    { title: 'Date Issued', value: 'creditNoteIssued', headerProps: { class: 'header-style' }, sortable: true },
    { title: 'Comment', value: 'comment', headerProps: { class: 'header-style' } }
]

const selectByQuarter = () => {
    if (isSelectByQuarter.value === true) {
        isSelectByNumber.value = false
        
        selectedCountries.value = []
        creditNoteNumber.value = ''

        selectedQuarters.value = quarters.value.length ? [quarters.value[0]] : []
    }
    else {
        isSelectByNumber.value = true
        selectedQuarters.value = []
        selectedCountries.value = []
    }
}

const selectByNumber = () => {
    if (isSelectByNumber.value === true) {
        isSelectByQuarter.value = false
        
        selectedQuarters.value = []
        selectedCountries.value = []
        creditNoteNumber.value = ''
    }
    else {
        isSelectByQuarter.value = true
        selectedQuarters.value = quarters.value.length ? [quarters.value[0]] : []
        creditNoteNumber.value = ''
    }
}

onMounted(() => {
    systemStore.getQuarters().then(result => {
        if (result.success) {
            quarters.value = result.quarters
                .filter(q => q.currentRedemptionQuarter === true || q.previousRedemptionQuarters === true)
                .map(q => q.quarter)

            const cq = result.quarters.find(q => q.currentRedemptionQuarter === true)?.quarter
            selectedQuarters.value = cq ? [cq] : []
            currentQuarter.value = cq
        }
    }).catch(error => {
        console.log('recalculateLocalValue', error)
    })

    registrationStore.GetEMEACountries().then(result => {
        countries.value = result.countries
    }).catch(error => {
        console.log('disti dashb get countries', error)
    })
})

const downloadFile1 = () => {
    systemStore.downloadCsvFile(reportData.value, 'creditNoteReport.csv')
}

const downloadFile = () => {
    if (reportData.value.length === 0) {
        return
    }
    
    excelStore.generateInMemoryExcelDocument(reportData.value, 'DistibutorCreditNoteDocument').catch(error => {
        console.log('Generate In Memory Excel Document - DistibutorCreditNoteDocument', error)
    })
}

const clickSubmit = () => {
    getReportData()
}

const selectedQuarterValues = () => {
    const v = selectedQuarters.value
    if (v == null) {
        return []
    }
    return Array.isArray(v) ? v : [v]
}

const selectedCountryValues = () => {
    const v = selectedCountries.value
    if (v == null) {
        return []
    }
    const arr = Array.isArray(v) ? v : [v]
    return arr
        .map((item) => {
            if (item == null) {
                return ''
            }
            if (typeof item === 'object' && item.country != null) {
                return String(item.country)
            }
            return String(item)
        })
        .filter((c) => c !== '')
}

const getReportData = () => {
    creditNoteNumber.value = creditNoteNumber.value != null ? creditNoteNumber.value : ''

    systemStore.getCreditNoteReport(selectedQuarterValues(), selectedCountryValues(), creditNoteNumber.value).then(result => {
        reportData.value = []
        if (result.success === true){
            reportData.value = result.creditNotes
        }
    }).catch(error => {
        console.log('credit note report, get data', error)
    })
}


</script>

<style>
.header-style{
   
    /* background-color: #012a4b; */
    background-color: #012a4b;
    color: white;
}

.v-data-table-header th {
  white-space: nowrap;
}

.v-data-table tbody tr:nth-child(even) {
      background-color: #e0e6ec;
      color: #202520;
}

.v-data-table tbody tr:nth-child(odd) {
      background-color: whitesmoke;
      color: #202520;
}
</style>